<script setup lang="ts">
import { defineAsyncComponent, defineComponent, ref } from 'vue'

import type { MediaViewerProps } from './types'

import { Icon } from '@/shared/ui/icon'
import { FloatingLayer, OverlayPortal } from '@/shared/ui/overlay'

// Lazy public entry for the swiper gallery/lightbox. This shell stays light (NO `swiper` import):
// the heavy gallery is pulled via defineAsyncComponent, so `swiper` splits into its own Vite chunk
// and never enters the main bundle (contract 02 §4, §16).
//
// The overlay chrome (backdrop + close) lives HERE, so the viewer is never a blank pane while the
// chunk loads, and a lazy-chunk load failure degrades to an inline error state WITH retry — a
// fresh `:key` re-runs the import (contract 02 §14).
withDefaults(defineProps<MediaViewerProps>(), {
  index: 0,
  prevLabel: 'ui.media.previous',
  nextLabel: 'ui.media.next',
  closeLabel: 'ui.media.close',
  loadErrorMessage: 'ui.media.loadError',
  retryLabel: 'ui.media.retry',
})

const emit = defineEmits<{ (event: 'close'): void }>()

const loadFailed = ref(false)
const attempt = ref(0)

const EmptyFailure = defineComponent({ name: 'MediaViewerLoadError', render: () => null })

const SwiperGallery = defineAsyncComponent({
  loader: () => import('./SwiperGallery.vue'),
  errorComponent: EmptyFailure,
  onError(_error, _retry, fail) {
    loadFailed.value = true
    fail()
  },
})

function retry(): void {
  loadFailed.value = false
  attempt.value += 1
}

function close(): void {
  emit('close')
}
</script>

<template>
  <OverlayPortal>
    <FloatingLayer
      kind="media"
      class="ui-lightbox fixed inset-0 flex items-center justify-center p-10 overflow-auto bg-media-backdrop"
      @click="close"
    >
      <div class="ui-lightbox__toolbar fixed top-6 end-6 flex gap-3" @click.stop>
        <button
          type="button"
          class="ui-lightbox__btn inline-flex items-center justify-center w-12 h-12 rounded-full text-on-media bg-media-control cursor-pointer transition-[background-color] duration-150 ease-[ease] hover:bg-media-control-hover"
          :aria-label="closeLabel"
          @click="close"
        >
          <Icon name="x" :size="20" />
        </button>
      </div>
      <div
        class="ui-lightbox__stage ui-gallery__stage flex h-4/5 max-h-[90vh] w-full max-w-[min(60rem,90vw)] items-center justify-center"
        @click.stop
      >
        <div
          v-if="loadFailed"
          class="ui-gallery__error flex flex-col items-center justify-center gap-sm text-center text-on-media-strong"
          role="alert"
        >
          <slot name="error" :retry="retry">
            <Icon class="ui-gallery__error-icon text-danger" name="alert-triangle" :size="40" />
            <p class="ui-gallery__error-text m-0 text-sm">{{ loadErrorMessage }}</p>
            <button
              type="button"
              class="ui-lightbox__btn inline-flex items-center justify-center w-12 h-12 rounded-full text-on-media bg-media-control cursor-pointer transition-[background-color] duration-150 ease-[ease] hover:bg-media-control-hover"
              :aria-label="retryLabel"
              @click="retry"
            >
              <Icon name="arrow-rotate-right" :size="18" />
            </button>
          </slot>
        </div>
        <SwiperGallery
          v-else
          :key="attempt"
          :items="items"
          :index="index"
          :prev-label="prevLabel"
          :next-label="nextLabel"
        />
      </div>
    </FloatingLayer>
  </OverlayPortal>
</template>
